import React, {useEffect, useState} from 'react';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    PlusCircleIcon
} from "@heroicons/react/24/solid";
import _ from 'lodash';
import {Promise} from "bluebird";
import {deleteSingleDesign, searchDesigns, updateSingleDesign} from "../../services/design-data-service";
import ProductFilters from "../../../pod/components/product/product-filters";
import {searchPods} from "../../../pod/services/pod-data-service";
import ShowTabsList from "../../../common/ui/show-tabs-list";
import {
    DEFAULT_DESIGN_DASHBOARD_TAB,
    DESIGN_DASHBOARD_TABS_LIST, PAGINATION_FIELD_LIST, REPEAT_IMAGE_FIELD_LIST
} from "../../constants/design.constant";
import ShowDesignVariantStatus from "../common/show-design-variant-status";
import ShowParentProductColorList from "../common/show-parent-product-color-list";
import {searchThemes} from "../../../theme/services/theme-data-service";
import {appConfig} from "../../../firebase/client-app";
import DownloadDesignVariants from "../download/download-design-variants";
import ShowDesignStatistics from "../common/show-design-statistics";
import {searchQuotesWithSearchCriteria} from "../../../common/services/quote-data-service";
import ShowDesignMetaDetail from "../common/show-design-meta-detail";
import {deleteSingleDesignVariant} from "../../services/design-variant-data-service";
import {processDesignVariantStatistics} from "../../utils/design-util";
import DeleteDesignVariant from "../design-variant/delete-design-variant";
import DeleteDesign from "../multi-products-designer/delete-design";
import RenderFields from "../../../common/ui/render-fields";
import {handleFieldValueChanges} from "../../../common/utils/field-change-util";
import CopyDataToClipboard from "../../../common/ui/copy-data-to-clipboard";

const tabsList = DESIGN_DASHBOARD_TABS_LIST;
const defaultPagination: any = {limit: '10'};

const DesignCardList = ({setDesignsCount}) => {
    const [searchText, setSearchText] = useState('');
    const [dataFetchInProgress, setDataFetchInProgress] = useState(true);
    const [designList, setDesignList] = useState([]);
    const [filterCriteria, setFilterCriteria] = useState(null);
    const [pagination, setPagination] = useState(defaultPagination);
    const [initialStep, setInitialStep] = useState(true);
    const [podList, setPodList] = useState([]);
    const [selectedPod, setSelectedPod] = useState(null);
    const [selectedTab, setSelectedTab] = useState(DEFAULT_DESIGN_DASHBOARD_TAB);
    const [themeList, setThemeList] = useState(null);
    const [quoteList, setQuoteList] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        init();
    }, [initialStep]);

    useEffect(() => {
        localStorage.setItem(appConfig?.appKey + ".designs.tab", JSON.stringify(selectedTab));
    }, [selectedTab]);

    useEffect(() => {
        localStorage.setItem(appConfig?.appKey + ".designs.pagination", JSON.stringify(pagination));
    }, [pagination]);

    const init = async () => {
        const {designFilters, currentPagination} = prepareInitialFilters();

        await Promise.all(
            [
                getAllThemeList(),
                getAllQuoteList(designFilters?.themeId),
                getAllPodList(designFilters?.podId)
            ]
        );

        await handleSearchDesigns(true, designFilters, currentPagination);
    }

    const prepareInitialFilters = () => {
        let designFilters: any = {};
        let currentTab: any = {};
        let currentPagination: any = {};
        const designFiltersText = localStorage.getItem(appConfig?.appKey + ".designFilters");
        const currentTabText = localStorage.getItem(appConfig?.appKey + ".designs.tab");
        const currentPaginationText = localStorage.getItem(appConfig?.appKey + ".designs.pagination");

        if (!_.isEmpty(designFiltersText) && designFiltersText !== 'undefined') {
            designFilters = JSON.parse(designFiltersText);
        }

        if (!_.isEmpty(designFilters)) {
            setFilterCriteria(designFilters);
        }

        if (!_.isEmpty(currentTabText) && currentTabText !== 'undefined') {
            currentTab = JSON.parse(currentTabText);
        }

        if (!_.isEmpty(currentTab)) {
            setSelectedTab(currentTab);
        }

        if (!_.isEmpty(currentPaginationText) && currentPaginationText !== 'undefined') {
            currentPagination = JSON.parse(currentPaginationText);
            setPagination(currentPagination);
        }

        return {designFilters, currentPagination};
    }

    const getAllPodList = async (podId) => {
        const podListResult = await searchPods('');
        setPodList(podListResult);

        const podsMap = _.keyBy(podListResult, 'id');
        setSelectedPod(podsMap[podId]);
    }

    const getAllThemeList = async () => {
        const themeListResult = await searchThemes('');
        setThemeList(_.orderBy(themeListResult, ['startText']));
    }

    const getAllQuoteList = async (themeId?) => {
        const quoteListResult = await searchQuotesWithSearchCriteria(prepareQuoteSearchCriteria(themeId));
        setQuoteList(_.orderBy(quoteListResult, ['startText']));
    }

    const prepareQuoteSearchCriteria = (themeId?) => {
        const searchCriteriaList = [];

        if (themeId) {
            searchCriteriaList.push({field: 'themes.' + themeId, condition: '==', value: true});
        }

        return searchCriteriaList;
    }

    const handleSearchDesigns = async (initial?, currentDesignFilters?, currentPagination?) => {
        let currentSelectedPod = {...selectedPod};
        let existingFilterCriteria = {...filterCriteria};
        let existingPagination = {...pagination};

        if (!_.isEmpty(currentDesignFilters)) {
            existingFilterCriteria = currentDesignFilters;
        }

        if (!_.isEmpty(currentPagination)) {
            existingPagination = currentPagination;
        }

        const searchCriteriaList = [];
        _.forIn(existingFilterCriteria, (value, field) => {
            if (!_.isEmpty(value)) {
                searchCriteriaList.push({field: field, condition: '==', value: value});
            }
        });

        if (existingFilterCriteria && _.isEmpty(existingFilterCriteria?.podId)) {
            if (!_.isEmpty(currentSelectedPod)) {
                searchCriteriaList.push({field: 'podId', condition: '==', value: currentSelectedPod?.id});
            }
        }

        let designsListResult = await searchDesigns(searchCriteriaList, _.parseInt(existingPagination?.limit), currentPage);
        designsListResult = _.map(designsListResult, (design) => {
            design.showVariants = false;
            return design;
        });


        setDesignList(designsListResult);
        setDataFetchInProgress(false);
        setDesignsCount(designsListResult.length);
    }

    const handleDetailChanges = (event) => {
        const existingFilterCriteria = {...filterCriteria};
        const {name, value} = event.target;
        existingFilterCriteria[name] = value;

        if (name === 'podId') {
            const existingPodList = [...podList];
            const podsMap = _.keyBy(existingPodList, 'id');
            setSelectedPod(podsMap[value]);
        } else if (name === 'themeId') {
            existingFilterCriteria['quoteId'] = '';
            getAllQuoteList(value);
            handleSearchDesigns(false, existingFilterCriteria);
        } else if (name === 'quoteId') {
            handleSearchDesigns(false, existingFilterCriteria);
        }

        setDesignFiltersInSession(existingFilterCriteria);
    }

    const handlePaginationChanges = (event) => {
        let existingPagination = {...pagination};
        existingPagination = handleFieldValueChanges(event, existingPagination);
        setPagination(existingPagination);

        handleSearchDesigns(false, null, existingPagination);
    }

    const toggleDesignVariantVisibility = (design, designIndex) => {
        const existingDesignList = [...designList];
        existingDesignList[designIndex].showVariants = !existingDesignList[designIndex].showVariants;
        setDesignList(existingDesignList);
    }

    const setDesignFiltersInSession = (currentFilterCriteria) => {
        setFilterCriteria(currentFilterCriteria);
        localStorage.setItem(appConfig?.appKey + ".designFilters", JSON.stringify(currentFilterCriteria));
    }

    const clearFilterCriteria = () => {
        setDesignFiltersInSession({})
    }

    const removeDesign = async (currentDesign) => {
        await deleteSingleDesign(currentDesign);

        const existingDesignList = [...designList];
        const currentDesignIndex = _.findIndex(existingDesignList, {id: currentDesign?.id});
        existingDesignList.splice(currentDesignIndex, 1);
        setDesignList(existingDesignList);
    }

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);

            handleSearchDesigns(false, null, null);
        }
    };

    const handleNextPage = () => {
        /*const totalPages = Math.ceil(totalItems / itemsPerPage);
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }*/

        setCurrentPage(currentPage + 1);
        handleSearchDesigns(false, null, null);
    };

    const handleMarkAsTemplateReady = async (design) => {
        const existingDesignList = [...designList];
        const currentDesignIndex = _.findIndex(existingDesignList, {id: design?.id});
        const existingDesignDetail = existingDesignList[currentDesignIndex];
        existingDesignDetail.isTemplateReady = true;
        existingDesignList[currentDesignIndex] = existingDesignDetail
        setDesignList(existingDesignList);

        delete existingDesignDetail['showVariants'];
        await updateSingleDesign(existingDesignDetail);
    };

    return (
        <>

            <div className="flex justify-start -mt-4">

                <div className="field-container">
                    <div className="field-label">
                        Theme
                    </div>
                    <select
                        className="input-field"
                        id="themeId"
                        name="themeId"
                        value={filterCriteria?.themeId}
                        onChange={handleDetailChanges}>
                        <option value="">Choose Theme</option>
                        {themeList?.map((theme, index) => (
                            <option key={index}
                                    value={theme?.id}>{theme?.startText}</option>
                        ))}
                    </select>
                </div>

                <div className="field-container">
                    <div className="field-label">
                        Quote
                    </div>
                    <select
                        className="input-field"
                        id="quoteId"
                        name="quoteId"
                        value={filterCriteria?.quoteId}
                        onChange={handleDetailChanges}>
                        <option value="">Choose Quote</option>
                        {quoteList?.map((quote, index) => (
                            <option key={index}
                                    value={quote?.id}>{quote?.startText}</option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-wrap justify-start">
                    <RenderFields
                        parentForm={pagination}
                        handleDetailChanges={handlePaginationChanges}
                        fieldList={_.groupBy(PAGINATION_FIELD_LIST, 'group')['pagination']}
                        enableEdit={true}
                        fieldClassName={'w-fit'}/>
                </div>

                <div className="field-container">
                    <div className="field-label">
                        POD <span
                        className="text-gray-400">({podList?.length})</span>
                    </div>
                    <select
                        className="input-field"
                        id="podId"
                        name="podId"
                        value={filterCriteria?.podId}
                        onChange={handleDetailChanges}>
                        <option value="">Choose POD</option>
                        {podList?.map((pod, index) => (
                            <option key={index}
                                    value={pod?.id}>{pod?.displayLabel}</option>
                        ))}
                    </select>
                </div>

                {selectedPod &&
                    <div className="">
                        <ProductFilters
                            searchProductsList={handleSearchDesigns}
                            filterCriteria={filterCriteria}
                            setFilterCriteria={setDesignFiltersInSession}
                            clearFilterCriteria={clearFilterCriteria}
                            podDetail={selectedPod}
                            hideColorsAndSizes={true}/>
                    </div>
                }

                {/*<div className={'flex text-center justify-center items-center mt-6'}>
                    <button onClick={handlePreviousPage}
                            className={`button-gray-outline p-1 ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={currentPage === 1}>
                        <ArrowLeftIcon className="h-6 w-6"/>
                    </button>
                    <div className={'mx-2'}>Page {currentPage}</div>
                    <button
                        className={'button-gray-outline p-1'}
                        onClick={handleNextPage}>
                        <ArrowRightIcon className="h-6 w-6"/>
                    </button>
                </div>*/}

            </div>

            <div className="mt-4">
                <ShowTabsList
                    setSelectedTab={setSelectedTab}
                    selectedTab={selectedTab}
                    tabsList={tabsList}/>
            </div>

            {selectedTab?.key === 'designs' &&

                <div
                    className="table-container overflow-y-scroll bg-blue-100 border border-gray-400 p-4 flex flex-wrap justify-start content-start mt-4">

                    {designList?.length === 0 &&
                        <div className="w-full h-96 flex items-center justify-center">
                            <div className="text-xl text-gray-600">
                                {!dataFetchInProgress &&
                                    <div>No Designs Available</div>
                                }
                                {dataFetchInProgress &&
                                    <div>Fetching Designs... </div>
                                }
                            </div>
                        </div>
                    }

                    {designList?.map((design, index) => (
                        <div key={index}
                             className="shadow-lg bg-white text-neutral-600 hover:bg-purple-50 hover:border hover:border-purple-600 rounded m-2 h-56 design-card">

                            <div className="px-4 pt-6">
                                <div className="h-24 -mt-2">
                                    <div className="text-lg">{design?.quoteName}</div>
                                    <div className="text-sm">{design?.themeName}</div>
                                    <div className="font-bold">{design?.parent}
                                        {design?.size &&
                                            <span className=""> | {design?.size}</span>
                                        }
                                    </div>
                                </div>

                                <div className="flex justify-between">
                                    <div className="mt-1">
                                        <div className="font-bold text-purple-700">{design?.podName}</div>
                                        <div className="text-sm">{design?.category}</div>
                                    </div>

                                    <ShowDesignStatistics designDetail={design}/>

                                    {/*{design?.variants?.length > 0 &&
                                        <div className="mt-1">
                                            <div
                                                className="font-bold text-purple-700 flex justify-center">{design?.variants?.length}</div>
                                            <div className="text-sm">Variants</div>
                                        </div>
                                    }

                                    {design?.statistics?.inDesign > 0 &&
                                        <div className="mt-1">
                                            <div
                                                className="font-bold text-gray-700 flex justify-center">{design?.statistics?.inDesign}</div>
                                            <div className="text-sm">In Design
                                            </div>
                                        </div>
                                    }

                                    {design?.statistics?.published > 0 &&
                                        <div className="mt-1">
                                            <div
                                                className="font-bold text-purple-700 flex justify-center">{design?.statistics?.published}</div>
                                            <div className="text-sm">Published
                                            </div>
                                        </div>
                                    }

                                    {design?.statistics?.readyToPublish > 0 &&
                                        <div className="mt-1">
                                            <div
                                                className="font-bold text-green-700 flex justify-center">{design?.statistics?.readyToPublish}</div>
                                            <div className="text-sm">Ready
                                                To Publish
                                            </div>
                                        </div>
                                    }*/}
                                </div>
                            </div>

                            <div className="flex justify-between px-3 py-2 mt-1">
                                <a href={`/designs/${design?.id}/edit`}
                                   className="button-outline-small flex w-full justify-center">
                                    <div>Edit</div>
                                </a>
                                <div className="mx-1"></div>
                                <a href={`/designs/${design?.id}/design`}
                                   className="button-outline-small flex w-full justify-center">
                                    <div>Design</div>
                                </a>
                                <div className="mx-1"></div>
                                <a href={`/designs/${design?.id}/publish`}
                                   className={`button-outline-small flex w-full justify-center ${design?.statistics?.readyToPublish > 0 ? 'button-green' : ''}`}>
                                    <div>Publish</div>
                                </a>

                                <div className="mx-1"></div>
                                <div>
                                    <DownloadDesignVariants
                                        designDetail={design}
                                        parentProduct={undefined}
                                        isAssignColors={undefined}
                                        isPreview={undefined}
                                        isLightButton={true}
                                        updateVariantColorsInDesign={undefined}
                                        setDesignDetail={undefined}/>
                                </div>

                            </div>

                        </div>
                    ))}
                </div>

            }

            {selectedTab?.key === 'design-variants' &&
                <div className="w-full">
                    <div className="table-container overflow-y-scroll bg-blue-100 border border-gray-400 p-4 mt-4">
                        {designList?.length === 0 &&
                            <div className="w-full h-96 flex items-center justify-center">
                                <div className="text-xl text-gray-600">
                                    {!dataFetchInProgress &&
                                        <div>No Designs Available</div>
                                    }
                                    {dataFetchInProgress &&
                                        <div>Fetching Designs... </div>
                                    }
                                </div>
                            </div>
                        }

                        {designList?.map((design, rowIndex) => (
                            <React.Fragment key={`row-${rowIndex}`}>

                                <div
                                    className={`my-2 border border-gray-400 ${design?.showVariants ? 'bg-neutral-400 text-white shadow-xl' : 'bg-white'}`}>

                                    <div className="p-2 align-middle flex justify-between">

                                        <div className="flex justify-start self-center">

                                            <div className="font-bold mx-2 self-center">{rowIndex + 1}</div>

                                            <div onClick={() => {
                                                toggleDesignVariantVisibility(design, rowIndex);
                                            }}
                                                 className="cursor-pointer flex justify-center">

                                                {!design?.showVariants &&
                                                    <div className="mt-3">
                                                        <ChevronRightIcon className="h-5 w-5"/>
                                                    </div>
                                                }
                                                {design?.showVariants &&
                                                    <div className="mt-3">
                                                        <ChevronDownIcon className="h-5 w-5"/>
                                                    </div>
                                                }
                                            </div>

                                            <div onClick={() => {
                                                toggleDesignVariantVisibility(design, rowIndex);
                                            }}
                                                 className={`ml-2 self-center cursor-pointer ${design?.showVariants ? 'font-semibold' : ''}`}>
                                                {/*<span className="font-bold mr-2 ">{rowIndex + 1}</span>*/}
                                                {design.name}
                                            </div>

                                            <div className="ml-2 flex justify-start items-center">
                                                {design?.podName &&
                                                    <div
                                                        className={`pill ${design?.showVariants ? 'pill-white' : 'pill-purple'}`}>
                                                        {design?.podName}
                                                    </div>
                                                }

                                                {design?.productType &&
                                                    <div
                                                        className={`pill ${design?.showVariants ? 'pill-white' : 'pill-purple'}`}>
                                                        {design?.productType}
                                                    </div>
                                                }

                                                {design?.category &&
                                                    <div
                                                        className={`pill ${design?.showVariants ? 'pill-white' : 'pill-purple'}`}>
                                                        {design?.category}
                                                    </div>
                                                }

                                                <ShowDesignMetaDetail designDetail={design}/>

                                                <div className="ml-2 flex justify-start">
                                                    <CopyDataToClipboard value={design?.quoteName} label={'Q'}/>
                                                    <CopyDataToClipboard value={design?.themeName} label={'T'}/>
                                                    <CopyDataToClipboard value={design?.parent} label={'P'}/>
                                                    <CopyDataToClipboard
                                                        value={_.replace(design?.parent, 'All-Over Print ', '')}
                                                        label={'P 2'}/>
                                                    <CopyDataToClipboard
                                                        value={_.replace(design?.parent, 'All-Over Print ', '') + ' Collection'}
                                                        label={'C'}/>
                                                    <CopyDataToClipboard value={design?.name} label={'D'}/>
                                                    <CopyDataToClipboard value={JSON.stringify(design, null, 2)}/>
                                                </div>

                                            </div>
                                        </div>

                                        {!design?.isTemplateReady &&
                                            <div className="flex justify-center self-center">
                                                <button
                                                    className={'button-sky-outline button-outline-small px-2 py-1 ml-2'}
                                                    onClick={() => handleMarkAsTemplateReady(design)}>
                                                    Mark As Templates Ready
                                                </button>
                                            </div>
                                        }

                                        {/*<div className="flex justify-end self-center">
                                            {design?.statistics?.readyToPublish > 0 &&
                                                <div className="text-center">
                                                    <div
                                                        className={`pill ${design?.showVariants ? 'pill-white' : 'pill-green'}`}>
                                                        <span
                                                            className="font-bold">{design?.statistics?.readyToPublish}</span> Ready
                                                        To Publish
                                                    </div>
                                                </div>
                                            }

                                            {design?.statistics?.published > 0 &&
                                                <div className="text-center">
                                                    <div
                                                        className={`pill ${design?.showVariants ? 'pill-white' : 'pill-green'}`}>
                                                        <span
                                                            className="font-bold">{design?.statistics?.published}</span> Published
                                                    </div>
                                                </div>
                                            }
                                        </div>*/}

                                        <div className="flex justify-start items-center">
                                            {design?.statistics?.readyToPublish > 0 &&
                                                <div className="text-center">
                                                    <div
                                                        className={`pill ${design?.showVariants ? 'pill-white' : 'pill-green'}`}>
                                                        <span
                                                            className="font-bold">{design?.statistics?.readyToPublish}</span> Ready
                                                        To Publish
                                                    </div>
                                                </div>
                                            }

                                            {design?.statistics?.published > 0 &&
                                                <div className="text-center">
                                                    <div
                                                        className={`pill ${design?.showVariants ? 'pill-white' : 'pill-green'}`}>
                                                        <span
                                                            className="font-bold">{design?.statistics?.published}</span> Published
                                                    </div>
                                                </div>
                                            }

                                            <div className="ml-4">
                                                <DownloadDesignVariants
                                                    designDetail={design}
                                                    parentProduct={undefined}
                                                    isAssignColors={undefined}
                                                    isPreview={undefined}
                                                    isLightButton={true}
                                                    updateVariantColorsInDesign={undefined}
                                                    setDesignDetail={undefined}/>
                                            </div>

                                            <div>
                                                <a href={`/designs/${design?.id}/publish`}
                                                   className={`ml-2 mr-2 py-2 px-3 button-outline-small flex justify-center ${design?.statistics?.readyToPublish > 0 ? 'button-green' : ''}`}>
                                                    <div>Publish</div>
                                                </a>
                                            </div>

                                            <div>
                                                <a href={`/designs/${design?.id}/design`}
                                                   className="button-outline-small py-2 px-3 flex justify-center">
                                                    <div>Design</div>
                                                </a>
                                            </div>

                                            <div className="float-right px-2">
                                                <DeleteDesign
                                                    design={design}
                                                    removeDesign={removeDesign}/>
                                            </div>
                                        </div>
                                    </div>

                                    {design?.showVariants &&

                                        <table className="w-full text-gray-500">
                                            <tbody className="">

                                            {design?.variants?.map((designVariant, index) => (
                                                <tr key={`row-${rowIndex}-variant-${index}`}
                                                    className="bg-white">
                                                    <td className="p-2 w-1/2 border-r border-t border-gray-400">
                                                        <div className="flex justify-start items-center">
                                                            <div
                                                                className="ml-2 font-semibold mr-2 text-purple-600">#{index + 1}</div>
                                                            <div
                                                                className="align-middle text-gray-600">{designVariant?.label}</div>

                                                            <div className="ml-2">
                                                                <CopyDataToClipboard value={designVariant?.label}/>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="p-2 w-40 border-r border-t border-gray-400">
                                                        <div className="align-middle">
                                                            <ShowDesignVariantStatus
                                                                designVariantDetail={designVariant}/>
                                                        </div>
                                                    </td>
                                                    <td className="p-2 border-t border-gray-400">
                                                        <div className="align-middle">
                                                            <ShowParentProductColorList parentProduct={designVariant}/>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}

                                            </tbody>
                                        </table>

                                    }

                                </div>

                            </React.Fragment>
                        ))}
                    </div>
                </div>
            }


        </>
    )
}

export default DesignCardList
